import React, { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";

interface UsageMetric {
  name: string;
  displayName: string;
  current: number;
  max: number;
  percent: number;
  isUnlimited: boolean;
}

interface SiteUsageSummary {
  siteId: number;
  metrics: UsageMetric[];
}

function getBarColor(percent: number, isUnlimited: boolean): string {
  if (isUnlimited) return "bg-cornflower-blue-400";
  if (percent >= 90) return "bg-crimson-red-500";
  if (percent >= 70) return "bg-sweet-orange-500";
  return "bg-emerald-green-500";
}

function getTextColor(percent: number, isUnlimited: boolean): string {
  if (isUnlimited) return "text-slate-gray-500";
  if (percent >= 90) return "text-crimson-red-600";
  if (percent >= 70) return "text-sweet-orange-600";
  return "text-emerald-green-600";
}

function getStatusLabel(percent: number, isUnlimited: boolean): string {
  if (isUnlimited) return "Unlimited";
  if (percent >= 100) return "At Limit";
  if (percent >= 90) return "Critical";
  if (percent >= 70) return "Warning";
  return "OK";
}

function getStatusBadge(percent: number, isUnlimited: boolean): string {
  if (isUnlimited) return "bg-slate-gray-100 text-slate-gray-600";
  if (percent >= 100) return "bg-crimson-red-100 text-crimson-red-700";
  if (percent >= 90) return "bg-crimson-red-50 text-crimson-red-600";
  if (percent >= 70) return "bg-sweet-orange-50 text-sweet-orange-600";
  return "bg-emerald-green-50 text-emerald-green-600";
}

const MONTHLY_METRIC_NAMES = [
  "conversations", "sms", "ivr_minutes", "report_exports", "report_emails", "sms_alerts",
  "emails_sent", "contacts_imported", "broadcasts_sent", "workflow_runs"
];

const RESOURCE_METRIC_NAMES = [
  "scheduled_reports", "custom_fields", "licensed_users", "created_users",
  "knowledge_indexes", "blocked_ips", "custom_statuses"
];

export default function UsageOverviewPanel() {
  const api = useApi();
  const [usage, setUsage] = useState<SiteUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFirstLoad = useRef(true);

  const fetchUsage = useCallback(async () => {
    if (isFirstLoad.current) setLoading(true);
    try {
      const resp = await api.messaging.get("admin/billing/usage");
      if (resp.ok) {
        const data = await resp.json();
        setUsage(data);
        setError(null);
      } else {
        setError("Failed to load usage data.");
      }
    } catch {
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false;
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchUsage();
    const interval = setInterval(fetchUsage, 60000);
    return () => clearInterval(interval);
  }, [fetchUsage]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-cornflower-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-crimson-red-50 border border-crimson-red-200 rounded-lg p-4 text-sm text-crimson-red-700">
          {error}
          <button
            onClick={fetchUsage}
            className="ml-3 text-cornflower-blue-500 hover:text-cornflower-blue-700 font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metrics = Array.isArray(usage?.metrics) ? usage!.metrics : [];
  const monthlyMetrics = metrics.filter(m => MONTHLY_METRIC_NAMES.includes(m.name));
  const staticMetrics = metrics.filter(m => RESOURCE_METRIC_NAMES.includes(m.name));
  const overLimit = metrics.filter(m => !m.isUnlimited && m.percent >= 100);
  const approaching = metrics.filter(m => !m.isUnlimited && m.percent >= 70 && m.percent < 100);

  return (
    <div className="p-6 space-y-6">
      {/* Over Limit Banner (red) */}
      {overLimit.length > 0 && (
        <div className="bg-crimson-red-50 border border-crimson-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-crimson-red-700 font-semibold text-sm">
              {overLimit.length} resource{overLimit.length > 1 ? "s" : ""} at or over limit
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {overLimit.map(m => (
              <span
                key={m.name}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-crimson-red-100 text-crimson-red-700"
              >
                {m.displayName}: {m.current.toLocaleString()} / {m.max.toLocaleString()} ({m.percent}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Approaching Limit Banner (amber) */}
      {approaching.length > 0 && (
        <div className="bg-sweet-orange-50 border border-sweet-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sweet-orange-600 font-semibold text-sm">
              {approaching.length} resource{approaching.length > 1 ? "s" : ""} approaching limit
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {approaching.map(m => (
              <span
                key={m.name}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sweet-orange-50 text-sweet-orange-600"
              >
                {m.displayName}: {m.current.toLocaleString()} / {m.max.toLocaleString()} ({m.percent}%)
              </span>
            ))}
          </div>
        </div>
      )}

      {overLimit.length === 0 && approaching.length === 0 && metrics.length > 0 && (
        <div className="bg-emerald-green-50 border border-emerald-green-200 rounded-lg p-4">
          <span className="text-emerald-green-700 font-medium text-sm">
            All resources within normal limits
          </span>
        </div>
      )}

      {/* Monthly Usage */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-gray-800">Monthly Usage</h3>
          <span className="text-[10px] text-slate-gray-400 font-mono">
            Resets 1st of each month
          </span>
        </div>
        <div className="space-y-4">
          {monthlyMetrics.map(m => (
            <MetricRow key={m.name} metric={m} />
          ))}
          {monthlyMetrics.length === 0 && (
            <div className="text-sm text-slate-gray-400">No monthly metrics available.</div>
          )}
        </div>
      </div>

      {/* Static Limits */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-gray-800">Resource Limits</h3>
          <span className="text-[10px] text-slate-gray-400 font-mono">
            Current counts vs. subscription limits
          </span>
        </div>
        <div className="space-y-4">
          {staticMetrics.map(m => (
            <MetricRow key={m.name} metric={m} />
          ))}
          {staticMetrics.length === 0 && (
            <div className="text-sm text-slate-gray-400">No resource limits available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricRow({ metric }: { metric: UsageMetric }) {
  const barWidth = Math.min(metric.percent, 100);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-slate-gray-700">{metric.displayName}</span>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${getTextColor(metric.percent, metric.isUnlimited)}`}>
            {metric.current.toLocaleString()}
            {!metric.isUnlimited && (
              <span className="text-slate-gray-400 font-normal"> / {metric.max.toLocaleString()}</span>
            )}
          </span>
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusBadge(metric.percent, metric.isUnlimited)}`}>
            {getStatusLabel(metric.percent, metric.isUnlimited)}
          </span>
        </div>
      </div>
      {!metric.isUnlimited && (
        <div className="w-full bg-slate-gray-100 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${getBarColor(metric.percent, metric.isUnlimited)}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
      )}
    </div>
  );
}
